import React, { useState, useEffect } from "react";
import { ArrowLeft, Plus, Package, Calendar, FileText, User, Building, MessageSquare, CheckCircle, Loader2, Upload } from "lucide-react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const GRNConEditGstEntry = () => {
  const [isDark, setIsDark] = useState(false);
  const [branchType, setBranchType] = useState("HO");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [itemDetails, setItemDetails] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [selectedItemDetails, setSelectedItemDetails] = useState(new Set());
  const [grnInsCode, setGrnInsCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    grnDate: new Date().toISOString().split('T')[0],
    grnNo: "",
    grnInsNo: '',
    refDate: '',
    invNo: '',
    invDate: '',
    invCopy: null,
    refNo: '',
    remarks: '',
    employeeId: '',
    deliveryType: 'Direct'
  });
  
  // Mock employees data - replace with actual API call
  const [employees, setEmployees] = useState([
    { id: '1', name: 'John Doe' },
    { id: '2', name: 'Jane Smith' },
    { id: '3', name: 'Mike Johnson' },
    { id: '4', name: 'Sarah Wilson' }
  ]);
  
  // Calculation states
  const [calculationData, setCalculationData] = useState({
    subTotal: 0,
    discount: 0,
    afterDiscountTotal: 0,
    taxValue: 0,
    isFreightApplicable: false,
    freightValue: 0,
    freightTaxType: "CGST+SGST",
    isInsuranceApplicable: false,
    insuranceValue: 0,
    insuranceTaxType: "CGST+SGST",
    isPFApplicable: false,
    pfValue: 0,
    pfTaxType: "CGST+SGST",
    cgst: 0,
    sgst: 0,
    igst: 0,
    roundOff: 0,
    otherCharges: 0,
    totalAmount: 0,
    loading: false
  });
  
  const location = useLocation();
  const navigate = useNavigate();

  // Handle different data formats for selectedPOs
  const selectedPOs = location.state && location.state.editItem 
    ? (Array.isArray(location.state.editItem) 
        ? location.state.editItem 
        : [location.state.editItem])
    : [];

  const firstPO = selectedPOs.length > 0 ? selectedPOs[0] : {};

  useEffect(() => {
    console.log("Received selected POs:", selectedPOs);
    if (selectedPOs.length > 0) {
      console.log("First PO structure:", selectedPOs[0]);
    }
  }, [selectedPOs]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, invCopy: file }));
      toast.success(`Invoice copy "${file.name}" uploaded successfully`);
    }
  };

  const fetchPurchaseOrderItems = async (purchaseId) => {
    console.log("Fetching items for purchase id:", purchaseId);
    setLoadingItems(true);

    try {
      const response = await fetch(`https://www.nissi-india.com/api/grnCon_editFetch/4`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("API Response:", result);

      // Handle different response structures
      const itemsData = result.data || result.items || result;
      
      if (!itemsData || itemsData.length === 0) {
        throw new Error("No items found in response");
      }

      // Transform items to match expected format
      return itemsData.map((item, index) => ({
        ...item,
        purchaseId: purchaseId,
        poNumber: selectedPOs.find(po => po.fld_grninsid === purchaseId)?.fld_grninsno || "",
        id: `${purchaseId}-${index}`,
        grnQty: '',
        specification: item.fld_specification || item.specification || '',
        remarks: item.fld_remarks || item.remarks || '',
        fld_itemcode: item.fld_itemcode || item.itemCode || '',
        fld_itemname: item.fld_itemname || item.itemName || '',
        fld_itemHSNNo: item.fld_itemHSNNo || item.hsnNo || '',
        fld_uomname: item.fld_uomname || item.uom || '',
        poqty: item.fld_POqty || item.orderQty || 0,
        fld_qty: item.fld_qty || item.pendingQty || 0,
        fld_ppoprice: item.fld_poprice || item.unitPrice || 0,
        fld_DS: item.fld_DS || item.ds || 0,
        fld_CST: item.fld_CST || item.cgst || 0,
        fld_VAT: item.fld_VAT || item.sgst || 0,
        fld_SC: item.fld_SC || item.igst || 0,
      }));
    } catch (error) {
      console.error("Error fetching purchase order items:", error);
      toast.error(`Failed to fetch items: ${error.message}`);
      return [];
    } finally {
      setLoadingItems(false);
    }
  };

  const handleCalculationInputChange = (field, value) => {
    setCalculationData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculateSummary = async () => {
    try {
      setCalculationData(prev => ({ ...prev, loading: true }));

      // Prepare items payload
      const itemsPayload = itemDetails
        .filter(item => selectedItemDetails.has(item.id))
        .map((item, index) => ({
          checked: true,
          acceptQty: item.grnQty || item.fld_POqty || 0,
          price: item.fld_poprice || 0,
          ds: item.fld_DS || 0,
          pf: item.fld_PF || 0,
          ed: item.fld_ED || 0,
          vat: item.fld_VAT || 0,
          cst: item.fld_CST || 0,
          sc: item.fld_SC || 0,
          index // helpful for matching perItem results
        }));

      // Prepare purchaseOrders payload
      const purchaseOrdersPayload = selectedPOs.map(po => ({
        fld_purchaseorderid: po.fld_grninsid,
        fld_othercharges: po.fld_othercharges || 0
      }));

      // Final payload
      const payload = {
        items: itemsPayload,
        purchaseOrders: purchaseOrdersPayload
      };

      console.log("Calculation API payload:", payload);

      const response = await axios.post(
        "https://www.nissi-india.com/api/grnCon_calc",
        payload
      );

      const { data } = response;
      console.log("Calculation API response:", data);

      if (data.success) {
        const summary = data.summary || {};
        const perItem = data.perItem || [];

        setCalculationData(prev => ({
          ...prev,
          subtotal: summary.subtotal || 0,
          sumds: summary.sumds || 0,
          sumpf: summary.sumpf || 0,
          sumed: summary.sumed || 0,
          sumvat: summary.sumvat || 0,
          sumcst: summary.sumcst || 0,
          sumsc: summary.sumsc || 0,
          taxValue: summary.taxValue || 0,
          freight: summary.freight || 0,
          other: summary.other || 0,
          transport: summary.transport || 0,
          calamt: summary.calamt || 0,
          total: summary.totalAmount || 0,
          perItem: perItem, // keep per-item breakdown if needed
          loading: false
        }));

        toast.success("Calculation completed successfully");
      } else {
        toast.error("Calculation failed: Invalid response");
      }
    } catch (error) {
      console.error("Calculation error:", error);
      toast.error(
        "Failed to calculate: " + (error.response?.data?.message || error.message)
      );
    } finally {
      setCalculationData(prev => ({ ...prev, loading: false }));
    }
  };

  const handleItemSelect = async (index) => {
    const newSelected = new Set(selectedItems);
    const po = selectedPOs[index];
    
    if (newSelected.has(index)) {
      newSelected.delete(index);
      setItemDetails(prev => 
        prev.filter(item => item.fld_purchaseorderid !== po.fld_grninsid)
      );
    } else {
      newSelected.add(index);
      setLoadingItems(true);
      
      try {
        const items = await fetchPurchaseOrderItems(po.fld_purchaseorderid);
        if (items && items.length > 0) {
          setItemDetails(prev => [...prev, ...items]);
          toast.success(`Loaded ${items.length} items from GRN ${po.fld_purchaseorderid}`);
        } else {
          toast.warning(`No items found for GRN ${po.fld_purchaseorderid}`);
        }
      } catch (error) {
        console.error("Error in handleItemSelect:", error);
        toast.error(`Failed to load items: ${error.message}`);
      }
    }
    setSelectedItems(newSelected);
  };

  const handleItemDetailSelect = (itemId) => {
    const newSelected = new Set(selectedItemDetails);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItemDetails(newSelected);
  };

  const handleItemDetailChange = (itemId, field, value) => {
    setItemDetails(prev => 
      prev.map(item => 
        item.id === itemId 
          ? { ...item, [field]: value }
          : item
      )
    );
  };

  useEffect(() => {
    const fetchGrnNo = async () => {
      try {
        const response = await axios.get(
          "https://www.nissi-india.com/api/grnConNo",
          { params: { id: selectedPOs[0]?.fld_grninsid } }
        ); 

        if (response.data && response.data.success && response.data.data.length > 0) {
          const grnInsCode = response.data.data[0].fld_grninsno;  
          setGrnInsCode(grnInsCode);
          
        } else {
          console.log("No GRN INS Code found");
        }
      } catch (error) {
        console.error("Error fetching GRN INS Code:", error);
      }
    };

    if (selectedPOs.length > 0) {
      fetchGrnNo();
    }
  }, [branchType, selectedPOs]);

  // 1️⃣ Fetch GRN Confirm Data for Edit
  useEffect(() => {
    const fetchEditData = async () => {
      try {
        const grnId = selectedPOs[0]?.fld_grnconfid || selectedPOs[0]?.fld_grnid;
        if (!grnId) return;
        
        const { data } = await axios.get(
          `https://www.nissi-india.com/api/grnCon_editFetch/${grnId}`
        );

        if (data.success && data.data.length > 0) {
          const header = data.data[0];
         

          // Fill header form
         

          // Fill items if available
          if (header.details && header.details.length > 0) {
            setItemDetails(header.details);
            // Select all items by default in edit mode
            const allItemIds = new Set(header.details.map(item => item.id));
            setSelectedItemDetails(allItemIds);
          }
          
          toast.success("Edit data loaded");
        }
      } catch (err) {
        toast.error("Failed to fetch GRN edit data");
        console.error(err);
      }
    };
    
    if (selectedPOs.length > 0) {
      fetchEditData();
    }
  }, [selectedPOs]);

  const toISO = (dateStr, fallbackTime = "00:00:00") => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const isoDate = date.toISOString().split("T")[0];
    return `${isoDate}T${fallbackTime}`;
  };

  const handleCreateGRN = async () => {
     if (!selectedPOs || selectedPOs.length === 0) {
    toast.error("No GRN selected to update");
    return;
  }

  setIsSubmitting(true);

  // Build payload for update API
  const payload = {
    Date: formData.grnDate, // e.g. "21/08/2025"
    Ref_no: formData.refNo || "",
    Ref_Date: formData.refDate || formData.grnDate,
    Remarks: formData.remarks || "",
    Userid: 37,
    modifieddate: new Date().toLocaleString("en-GB", { hour12: false }), 
    grnconfid: selectedPOs[0]?.fld_grnconfid || 0,
    employeeid: formData.employeeId || "",
    fld_deliverytype: formData.deliveryType || "Direct",
    grntime: formData.grnTime || new Date().toLocaleTimeString("en-GB", { hour12: false })
  };

  console.log("Submitting GRN Update Payload:", JSON.stringify(payload, null, 2));

  try {
    const response = await axios.post(
      "https://www.nissi-india.com/api/grnCon_update",
      payload
    );

    if (response.data && response.data.success) {
      toast.success(`GRN ${formData.grnNo} updated successfully!`);
      setTimeout(() => {
        navigate(-1); // go back to listing
      }, 2000);
    } else {
      throw new Error(response.data.message || "Failed to update GRN");
    }
  } catch (error) {
    console.error("GRN update failed:", error);
    if (error.response) {
      console.error("Server error response:", error.response.data);
      toast.error(
        `Server error: ${error.response.data.message || "Unknown error"}`
      );
    } else {
      toast.error(`Network error: ${error.message}`);
    }
  } finally {
    setIsSubmitting(false);
  }
};
  const handleBack = () => {
    navigate(-1);
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="GOODS RECEIPT NOTE ENTRY" />
      <ToastContainer position="top-right" />
      <div className={`min-h-screen transition-all duration-500 ${
        isDark 
          ? "bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" 
          : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50"
      }`}>
        {/* Header */}
        <div className={`sticky top-0 z-40 backdrop-blur-xl border-b transition-all duration-300 ${
          isDark 
            ? "bg-gray-900/80 border-gray-700/50" 
            : "bg-white/80 border-gray-200/50"
        }`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBack}
                  className={`group flex items-center gap-2 px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    isDark
                      ? "bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500"
                      : "bg-white hover:bg-gray-50 text-gray-600 hover:text-gray-900 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
                  }`}
                >
                  <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                  Back to Listing
                </button>
                
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-xl ${
                    isDark ? "bg-indigo-900/50 text-indigo-300" : "bg-indigo-100 text-indigo-600"
                  }`}>
                    <Package className="w-6 h-6" />
                  </div>
                  <div>
                    <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                      Goods Receipt Note Entry
                    </h1>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      Create GRN from selected purchase orders
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto p-6">
          <div className={`rounded-2xl shadow-2xl border backdrop-blur-xl transition-all duration-500 transform hover:shadow-3xl ${
            isDark
              ? "bg-gray-800/90 border-gray-700/50 text-white"
              : "bg-white/90 border-gray-200/50 text-gray-900"
          }`}>
            
            {/* Form Header */}
            <div className={`p-6 border-b rounded-t-2xl ${
              isDark 
                ? "bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border-gray-700/50" 
                : "bg-gradient-to-r from-indigo-50 to-purple-50 border-gray-200/50"
            }`}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <FileText className={`w-5 h-5 ${isDark ? "text-indigo-300" : "text-indigo-600"}`} />
                  <h2 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                    GRN Information
                  </h2>
                </div>
                
                {/* Header Add Button */}
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 transform hover:scale-105 ${
                    isDark
                      ? "bg-green-700 hover:bg-green-600 text-white"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  } shadow-lg hover:shadow-xl`}
                >
                  <Plus className="w-4 h-4" />
                  Add Header
                </button>
              </div>
              
              {/* Branch Type Radio */}
              <div className="flex items-center gap-6">
                <span className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  Branch Type:
                </span>
                {["HO", "Branch"].map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      value={type}
                      checked={branchType === type}
                      onChange={() => setBranchType(type)}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 focus:ring-2"
                    />
                    <span className={`text-sm transition-colors group-hover:text-indigo-600 ${
                      branchType === type 
                        ? (isDark ? "text-indigo-300" : "text-indigo-600 font-medium")
                        : (isDark ? "text-gray-300" : "text-gray-700")
                    }`}>
                      {type}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Form Fields */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
                {/* GRN Date */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <Calendar className="w-4 h-4" />
                    GRN Date *
                  </label>
                  <input
                    type="date"
                    value={formData.grnDate}
                    onChange={(e) => handleInputChange('grnDate', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* GRN No */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <FileText className="w-4 h-4" />
                    GRN No
                  </label>
                  <input
                    type="text"
                    value={formData.grnNo}
                    readOnly
                    className={`w-full px-4 py-3 rounded-xl border ${
                      isDark
                        ? "bg-gray-600 border-gray-500 text-gray-300"
                        : "bg-gray-100 border-gray-200 text-gray-700"
                    }`}
                  />
                </div>

                {/* GRN Ins No */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <FileText className="w-4 h-4" />
                    GRN Ins No
                  </label>
                  <input
                    type="text"
                    value={firstPO.fld_grninsno}
                    onChange={(e) => handleInputChange('grnInsNo', e.target.value)}
                    placeholder="Enter GRN Ins Number"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Ref Date */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <Calendar className="w-4 h-4" />
                    Ref Date
                  </label>
                  <input
                    type="date"
                    value={formData.refDate}
                    onChange={(e) => handleInputChange('refDate', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Supplier */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <User className="w-4 h-4" />
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    value={firstPO.fld_suppliername || ""}
                    readOnly
                    placeholder="No supplier selected"
                    className={`w-full px-4 py-3 rounded-xl border ${
                      isDark
                        ? "bg-gray-600 border-gray-500 text-gray-300"
                        : "bg-gray-100 border-gray-200 text-gray-700"
                    }`}
                  />
                </div>

                {/* Invoice No */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <FileText className="w-4 h-4" />
                    Invoice No *
                  </label>
                  <input
                    type="text"
                    value={formData.invNo}
                    onChange={(e) => handleInputChange('invNo', e.target.value)}
                    placeholder="Enter Invoice Number"
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Invoice Date */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <Calendar className="w-4 h-4" />
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    value={formData.invDate}
                    onChange={(e) => handleInputChange('invDate', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Invoice Copy Upload */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <Upload className="w-4 h-4" />
                    Invoice Copy
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                      id="invoice-copy-upload"
                    />
                    <label
                      htmlFor="invoice-copy-upload"
                      className={`w-full px-4 py-3 rounded-xl border border-dashed cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:border-indigo-500 ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.invCopy ? formData.invCopy.name : "Upload Invoice"}
                    </label>
                  </div>
                </div>

                {/* Employee Dropdown */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <User className="w-4 h-4" />
                    Employee *
                  </label>
                  <select
                    value={formData.employeeId}
                    onChange={(e) => handleInputChange('employeeId', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select Employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Delivery Type Dropdown */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <Package className="w-4 h-4" />
                    Delivery Type *
                  </label>
                  <select
                    value={formData.deliveryType}
                    onChange={(e) => handleInputChange('deliveryType', e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="Direct">Direct Delivery</option>
                    <option value="Warehouse">Warehouse Delivery</option>
                    <option value="DropShip">Drop Ship</option>
                    <option value="PickUp">Customer Pick Up</option>
                  </select>
                </div>

                {/* Project Name */}
                <div className="group">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <Building className="w-4 h-4" />
                    Project Name
                  </label>
                  <input
                    type="text"
                    value={firstPO.fld_projectname || ""}
                    readOnly
                    placeholder="No project selected"
                    className={`w-full px-4 py-3 rounded-xl border ${
                      isDark
                        ? "bg-gray-600 border-gray-500 text-gray-300"
                        : "bg-gray-100 border-gray-200 text-gray-700"
                    }`}
                  />
                </div>

                {/* Ref No as Textarea - Full Width */}
                <div className="group md:col-span-2 lg:col-span-3 xl:col-span-4">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <FileText className="w-4 h-4" />
                    Reference No
                  </label>
                  <textarea
                    value={formData.refNo}
                    onChange={(e) => handleInputChange('refNo', e.target.value)}
                    placeholder="Enter reference numbers, PO numbers, or other reference details..."
                    rows={3}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {/* Remarks as Textarea - Full Width */}
                <div className="group md:col-span-2 lg:col-span-3 xl:col-span-4">
                  <label className={`flex items-center gap-2 text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    <MessageSquare className="w-4 h-4" />
                    Remarks
                  </label>
                  <textarea
                    value={formData.remarks}
                    onChange={(e) => handleInputChange('remarks', e.target.value)}
                    placeholder="Enter any additional remarks, notes, or special instructions..."
                    rows={4}
                    className={`w-full px-4 py-3 rounded-xl border transition-all duration-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              {/* Items Table */}
              <div className={`rounded-xl border overflow-hidden shadow-lg ${
                isDark ? "border-gray-600" : "border-gray-200"
              }`}>
                <div className={`px-6 py-4 border-b ${
                  isDark 
                    ? "bg-gray-700 border-gray-600" 
                    : "bg-gray-50 border-gray-200"
                }`}>
                  <h3 className={`text-lg font-semibold flex items-center gap-2 ${
                    isDark ? "text-white" : "text-gray-900"
                  }`}>
                    <Package className="w-5 h-5" />
                    Purchase Orders ({selectedPOs.length} orders)
                  </h3>
                  <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Select purchase orders to load item details
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={`${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                      <tr>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>Select</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>S.No</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>GRN No</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>Date</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>Supplier</th>
                        <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>Due Date</th>
                        <th className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                          isDark ? "text-gray-300" : "text-gray-500"
                        }`}>Status</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                      {selectedPOs.length > 0 ? selectedPOs.map((item, index) => (
                        <tr 
                          key={index} 
                          className={`transition-all duration-200 hover:bg-opacity-50 ${
                            selectedItems.has(index)
                              ? (isDark ? "bg-indigo-900/30" : "bg-indigo-50")
                              : (isDark ? "hover:bg-gray-700" : "hover:bg-gray-50")
                          }`}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.has(index)}
                                onChange={() => handleItemSelect(index)}
                                disabled={loadingItems}
                                className="w-5 h-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded transition-all duration-200 disabled:opacity-50"
                              />
                              {loadingItems && selectedItems.has(index) && (
                                <Loader2 className="w-4 h-4 ml-2 animate-spin text-indigo-600" />
                              )}
                            </div>
                          </td>
                          <td className={`px-4 py-4 text-sm font-medium ${
                            isDark ? "text-gray-200" : "text-gray-900"
                          }`}>
                            {index + 1}
                          </td>
                          <td className={`px-4 py-4 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {item.fld_grnconfno || "N/A"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {item.fld_date || item.date || "N/A"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {item.fld_suppliername || "N/A"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            {item.fld_duedate || item.dueDate || "N/A"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              item.Status === "Active" || item.status === "Active"
                                ? (isDark ? "bg-green-900 text-green-300" : "bg-green-100 text-green-800") 
                                : item.Status === "Pending" || item.status === "Pending"
                                ? (isDark ? "bg-yellow-900 text-yellow-300" : "bg-yellow-100 text-yellow-800")
                                : (isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-800")
                            }`}>
                              {item.Status || item.status || "Unknown"}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="7" className={`px-4 py-8 text-center text-sm ${
                            isDark ? "text-gray-400" : "text-gray-500"
                          }`}>
                            <div className="flex flex-col items-center gap-2">
                              <Package className="w-8 h-8 opacity-50" />
                              <span>No purchase orders available</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Item Details Table */}
              {Array.isArray(itemDetails) && itemDetails.length > 0 && (
                <div className={`rounded-xl border overflow-hidden shadow-lg mt-6 ${
                  isDark ? "border-gray-600" : "border-gray-200"
                }`}>
                  <div className={`px-6 py-4 border-b ${
                    isDark 
                      ? "bg-gray-700 border-gray-600" 
                      : "bg-gray-50 border-gray-200"
                  }`}>
                    <h3 className={`text-lg font-semibold flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}>
                      <FileText className="w-5 h-5" />
                      Item Details ({itemDetails.length} items)
                    </h3>
                    <p className={`text-sm mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      Review and select items for GRN creation
                    </p>
                    {loadingItems && (
                      <div className="flex items-center gap-2 mt-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                          Loading item details...
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${isDark ? "bg-gray-700" : "bg-gray-100"}`}>
                        <tr>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Select</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>S.No</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Item Code</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Item Name</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>HSN No</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>UOM</th>
                          <th className={`px-3 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Order Qty</th>
                          <th className={`px-3 py-3 text-center text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          
                          }`}>GRN Qty</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Accepted</th>
                          <th className={`px-3 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Remarks</th>
                          <th className={`px-3 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>Unit Price</th>
                          <th className={`px-3 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>DS%</th>
                          <th className={`px-3 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>CGST%</th>
                          <th className={`px-3 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>SGST%</th>
                          <th className={`px-3 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                            isDark ? "text-gray-300" : "text-gray-500"
                          }`}>IGST%</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
                        {itemDetails.map((item, index) => (
                          <tr 
                            key={item.id} 
                            className={`transition-all duration-200 hover:bg-opacity-50 ${
                              selectedItemDetails.has(item.id)
                                ? (isDark ? "bg-indigo-900/30" : "bg-indigo-50")
                                : (isDark ? "hover:bg-gray-700" : "hover:bg-gray-50")
                            }`}
                          >
                            <td className="px-3 py-4">
                              <input
                                type="checkbox"
                                checked={selectedItemDetails.has(item.fld_purchaseorderid)}
                                onChange={() => handleItemDetailSelect(item.fld_purchaseorderid)}
                                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                              />
                            </td>
                            <td className={`px-3 py-4 text-sm font-medium ${
                              isDark ? "text-gray-200" : "text-gray-900"
                            }`}>
                              {index + 1}
                            </td>
                            <td className={`px-3 py-4 text-sm font-mono ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_itemcode || item.itemCode || "N/A"}
                            </td>
                            <td className={`px-3 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_itemname || item.itemName || "N/A"}
                            </td>
                            <td className={`px-3 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_itemHSNNo || item.hsnNo || "N/A"}
                            </td>
                            <td className={`px-3 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_uomname || item.uom || "N/A"}
                            </td>
                            <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_POqty || item.orderQty || "0"}
                            </td>
                          
                             <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_grnqty}
                            </td>
                          <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_acceptedqty}
                               
                            </td>
                             <td className={`px-3 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                             {item.fld_remarks}
                               
                            </td>
                            <td className={`px-3 py-4 text-sm text-right ${isDark ? "text-green-400" : "text-green-600"}`}>
                              {item.fld_poprice || item.unitPrice || "0.00"}
                            </td>
                            <td className={`px-3 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_DS || item.ds || "0"}%
                            </td>
                            <td className={`px-3 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_CST || item.cgst || "0"}%
                            </td>
                            <td className={`px-3 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_VAT || item.sgst || "0"}%
                            </td>
                            <td className={`px-3 py-4 text-sm text-right ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                              {item.fld_SC || item.igst || "0"}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    
                    {/* Calculate Button */}
                    <div className="p-4">
                      <button
                        onClick={handleCalculateSummary}
                        disabled={selectedItemDetails.size === 0 || calculationData.loading}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                          isDark 
                            ? "bg-indigo-700 hover:bg-indigo-600 text-white" 
                            : "bg-indigo-100 hover:bg-indigo-200 text-indigo-700"
                        } ${selectedItemDetails.size === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {calculationData.loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" />
                        )}
                        {calculationData.loading ? "Calculating..." : "Calculate Summary"}
                      </button>
                    </div>
                  </div>

                  {/* Calculation Summary Section */}
                  <div className={`p-6 border-t ${
                    isDark ? "border-gray-600 bg-gray-700/50" : "border-gray-200 bg-gray-50"
                  }`}>
                    <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}>
                      <FileText className="w-5 h-5" />
                      Calculation Summary
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Base Total - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Base Total
                        </label>
                        <input
                          type="number"
                          value={calculationData.baseTotal || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* Discount Total - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Discount Total
                        </label>
                        <input
                          type="number"
                          value={calculationData.discountTotal || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* Subtotal - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Subtotal
                        </label>
                        <input
                          type="number"
                          value={calculationData.subtotal || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* PF Amount - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Packing & Forwarding
                        </label>
                        <input
                          type="number"
                          value={calculationData.pfAmount || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* Freight - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Freight Charges
                        </label>
                        <input
                          type="number"
                          value={calculationData.freight || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* Insurance - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Insurance
                        </label>
                        <input
                          type="number"
                          value={calculationData.insurance || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* CGST - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          CGST
                        </label>
                        <input
                          type="number"
                          value={calculationData.cgst || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* SGST - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          SGST
                        </label>
                        <input
                          type="number"
                          value={calculationData.sgst || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* IGST - Read Only */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          IGST
                        </label>
                        <input
                          type="number"
                          value={calculationData.igst || 0}
                          readOnly
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-gray-300"
                              : "bg-gray-100 border-gray-200 text-gray-700"
                          }`}
                        />
                      </div>

                      {/* TCS - Editable */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          TCS (%)
                        </label>
                        <input
                          type="number"
                          value={calculationData.tcs || 0.075}
                          onChange={(e) => handleCalculationInputChange('tcsPercent', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-600 border-gray-500 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      {/* Round Off - Editable */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Round Off
                        </label>
                        <input
                          type="number"
                          value={calculationData.roundOff || 0}
                          onChange={(e) => handleCalculationInputChange('roundOff', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-600 border-gray-500 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      {/* Other Charges - Editable */}
                      <div className="group">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Other Charges
                        </label>
                        <input
                          type="number"
                          value={calculationData.otherCharges || 0}
                          onChange={(e) => handleCalculationInputChange('otherCharges', parseFloat(e.target.value) || 0)}
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-600 border-gray-500 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      {/* Total Amount - Read Only */}
                      <div className="group col-span-1 md:col-span-2 lg:col-span-4">
                        <label className={`block text-sm font-medium mb-1 ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}>
                          Grand Total
                        </label>
                        <input
                          type="number"
                          value={calculationData.total || 0}
                          readOnly
                          className={`w-full px-3 py-3 rounded-lg border font-bold ${
                            isDark
                              ? "bg-green-900/50 border-green-700 text-green-300"
                              : "bg-green-100 border-green-200 text-green-800"
                          }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between items-center mt-8 p-6">
                    <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {selectedItems.size} PO(s) selected | {selectedItemDetails.size} item(s) selected for GRN
                    </div>
                    
                    <button
                      onClick={handleCreateGRN}
                      disabled={selectedItemDetails.size === 0 || isSubmitting}
                      className={`group flex items-center gap-3 px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                        selectedItemDetails.size > 0 && !isSubmitting
                          ? "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-lg hover:shadow-xl"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {isSubmitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle className="w-5 h-5 transition-transform group-hover:rotate-12" />
                      )}
                      {isSubmitting ? "Submitting..." : "Submit GRN"}
                      {selectedItemDetails.size > 0 && !isSubmitting && (
                        <span className="bg-white/20 px-2 py-1 rounded-full text-xs">
                          {selectedItemDetails.size}
                        </span>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GRNConEditGstEntry;